import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { 
  Monitor, 
  Headphones, 
  Cloud, 
  TrendingUp, 
  Lock, 
  GraduationCap,
  Shield,
  Zap,
  Globe,
  Server
} from "lucide-react";
import { RevealOnScroll, StaggerContainer, StaggerItem } from "../effects/AnimatedElements";
import { useLanguage } from "@/context/LanguageContext";

const ICON_MAP = {
  Monitor, Headphones, Cloud, TrendingUp, Lock, GraduationCap, Shield, Zap, Globe, Server
};

const defaultContent = {
  section_label: "Lo Que Ofrecemos",
  title: "Soluciones Integrales",
  subtitle: "No vendemos simplemente software. Entregamos soluciones completas que transforman la manera en que las empresas operan.",
  services: [
    { id: "so-personalizado", icon: "Monitor", title: "Sistema Operativo Personalizado", description: "Plataforma completa adaptada 100% a las necesidades de tu negocio. Panel de administración, gestión de clientes, facturación electrónica y reportes en tiempo real.", color: "#3b82f6" },
    { id: "soporte-24-7", icon: "Headphones", title: "Soporte Técnico 24/7", description: "Equipo de ingenieros disponible para asistencia continua. Actualizaciones y mejoras constantes del sistema.", color: "#06b6d4" },
    { id: "infraestructura-cloud", icon: "Cloud", title: "Infraestructura en la Nube", description: "Servidores seguros con respaldo automático. Acceso desde cualquier dispositivo, en cualquier lugar del mundo.", color: "#8b5cf6" },
    { id: "escalabilidad", icon: "TrendingUp", title: "Escalabilidad Total", description: "El sistema crece con tu negocio. Agrega sucursales, usuarios y funcionalidades sin límites.", color: "#10b981" },
    { id: "seguridad", icon: "Lock", title: "Confidencialidad Absoluta", description: "Toda tu información protegida bajo estrictos protocolos de seguridad y acuerdos de confidencialidad.", color: "#f43f5e" },
    { id: "capacitacion", icon: "GraduationCap", title: "Capacitación Completa", description: "Entrenamiento integral para ti y tu equipo, asegurando el máximo aprovechamiento de todas las funcionalidades.", color: "#f59e0b" }
  ]
};

const ServicesSection = ({ content }) => {
  const data = { ...defaultContent, ...content };
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });
  const { t } = useLanguage();

  return (
    <section
      id="servicios"
      ref={sectionRef}
      data-testid="services-section"
      className="py-24 lg:py-32 relative overflow-hidden"
    >
      {/* Background elements */}
      <div className="absolute inset-0">
        <motion.div
          className="absolute top-0 right-0 w-96 h-96 bg-[#3b82f6]/5 rounded-full blur-3xl"
          animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 8, repeat: Infinity }}
        />
        <motion.div
          className="absolute bottom-0 left-0 w-96 h-96 bg-[#06b6d4]/5 rounded-full blur-3xl"
          animate={{ scale: [1.2, 1, 1.2], opacity: [0.5, 0.3, 0.5] }}
          transition={{ duration: 8, repeat: Infinity }}
        />
      </div>

      <div className="section-container relative z-10">
        {/* Section Header with animation */}
        <RevealOnScroll direction="up" className="text-center mb-16">
          <motion.span 
            className="text-[#3b82f6] text-sm font-semibold tracking-wider uppercase mb-4 inline-block"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
          >
            {t("services_label")}
          </motion.span>
          <motion.h2 
            data-testid="services-title"
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6"
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            {t("services_title")}
          </motion.h2>
          <motion.p 
            className="text-[#94a3b8] text-lg max-w-2xl mx-auto"
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {t("services_subtitle")}
          </motion.p>
          <motion.p 
            className="text-[#3b82f6]/50 text-sm font-semibold tracking-widest uppercase mt-3"
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            Global Metafora APP
          </motion.p>
        </RevealOnScroll>

        {/* Bento Grid with stagger animation */}
        <StaggerContainer staggerDelay={0.1} className="bento-grid">
          {data.services.map((service, index) => {
            const IconComponent = ICON_MAP[service.icon] || Monitor;
            const isLarge = index === 0;
            
            return (
              <StaggerItem key={service.id}>
                <motion.div
                  data-testid={`service-card-${service.id}`}
                  className={`glass-card p-8 group relative overflow-hidden cursor-pointer ${isLarge ? "bento-item-large" : ""}`}
                  whileHover={{ 
                    scale: 1.02,
                    transition: { duration: 0.3 }
                  }}
                >
                  {/* Animated border gradient */}
                  <motion.div
                    className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                    style={{
                      background: `linear-gradient(135deg, ${service.color}20, transparent, ${service.color}10)`,
                    }}
                  />

                  {/* Top line animation */}
                  <motion.div
                    className="absolute top-0 left-0 h-[2px] bg-gradient-to-r from-transparent via-current to-transparent"
                    style={{ color: service.color }}
                    initial={{ width: "0%", left: "50%" }}
                    whileHover={{ width: "100%", left: "0%" }}
                    transition={{ duration: 0.5 }}
                  />

                  {/* Icon with animation */}
                  <motion.div 
                    className="w-14 h-14 flex items-center justify-center mb-6 transform -skew-x-6 relative"
                    style={{ backgroundColor: `${service.color}20` }}
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    <IconComponent 
                      className="w-7 h-7 skew-x-6 relative z-10" 
                      style={{ color: service.color }}
                    />
                    <motion.div
                      className="absolute inset-0"
                      style={{ backgroundColor: service.color }}
                      initial={{ scale: 0, opacity: 0 }}
                      whileHover={{ scale: 1.5, opacity: 0.2 }}
                      transition={{ duration: 0.3 }}
                    />
                  </motion.div>

                  {/* Content */}
                  <motion.h3 
                    className="text-xl font-bold text-white mb-3 group-hover:text-transparent group-hover:bg-clip-text"
                    style={{ 
                      backgroundImage: `linear-gradient(to right, white, ${service.color})`,
                      WebkitBackgroundClip: "text"
                    }}
                  >
                    {service.title}
                  </motion.h3>
                  <p className="text-[#94a3b8] leading-relaxed group-hover:text-[#b4c4d8] transition-colors">
                    {service.description}
                  </p>

                  {/* Hover corner accent */}
                  <motion.div 
                    className="absolute bottom-0 right-0 w-24 h-24"
                    style={{
                      background: `linear-gradient(135deg, transparent 50%, ${service.color}15 50%)`
                    }}
                    initial={{ opacity: 0, scale: 0 }}
                    whileHover={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.3 }}
                  />

                  {/* Floating particles on hover */}
                  <motion.div
                    className="absolute top-4 right-4 w-2 h-2 rounded-full"
                    style={{ backgroundColor: service.color }}
                    initial={{ opacity: 0 }}
                    whileHover={{ 
                      opacity: [0, 1, 0],
                      y: [0, -20],
                      x: [0, 10]
                    }}
                    transition={{ duration: 1, repeat: Infinity }}
                  />
                </motion.div>
              </StaggerItem>
            );
          })}
        </StaggerContainer>
      </div>
    </section>
  );
};

export default ServicesSection;
